import DataBase from "./data_base";
import GraphContainer, { getOrSet, getOrSetVec } from "../graph_container";
import createLabelFilter from "../create-label-filter";

class DataPoint extends DataBase {
    _labelPosition = "none";
    _label = "";
    _radius = 3;
    _position = new THREE.Vector2();
    _origin = new THREE.Vector2();
    _rect = GraphContainer.create("rect")
    _text = GraphContainer.create("text");
    _sphere = GraphContainer.create("circle");
    _colorText = "white";
    _colorTextShadow = "black";

    constructor(graphing, position = new THREE.Vector2()) {
        super(graphing);

        this._defs = GraphContainer.create("defs");
        this._position.copy(position);

        this.append(this._sphere, this._text, this._defs);

        const { feFlood, filter } = createLabelFilter(this._uuid);

        this._feFlood = feFlood;
        this._filter = filter;

        this._defs.append(this._filter);
    }

    _serialize() {
        return {
            position: this._position.toArray(),
            radius: this._radius,
            label: {
                caption: this._label,
                position: this._labelPosition
            }
        };
    }

    get type() { return "point"; }
    get defaultName() { return "Point"; }

    /**
     * @returns {this}
     */
    colorText() { return getOrSet.call(this, this, "_colorText", ...arguments); }

    /**
     * @returns {this}
     */
    colorTextShadow() { return getOrSet.call(this, this, "_colorTextShadow", ...arguments); }

    /**
     * @returns {this}
     */
    labelPosition() { return getOrSet.call(this, this, "_labelPosition", ...arguments); }
    /**
     * @returns {this}
     */
    label() { return getOrSet.call(this, this, "_label", ...arguments); }

    /**
     * @returns {this}
     */
    radius() { return getOrSet.call(this, this, "_radius", ...arguments); }

    /**
     * @returns {this}
     */
    origin() { return getOrSetVec.call(this, "_origin", ...arguments); }

    /**
     * @returns {this}
     */
    position() { return getOrSetVec.call(this, "_position", ...arguments); }

    redraw() {
        let offsetX = 0, offsetY = 0;

        this._sphere
            .setAttribute("name", this._name)
            .setAttribute("r", this._radius)
            .setAttribute("cx", this._origin.x)
            .setAttribute("cy", this._origin.y)
            .setAttribute("fill", this._color);

        if (this._labelPosition !== "none") {
            switch (this._labelPosition) {
                case "bottom":
                    offsetY = 16 + this._radius;
                    this._text
                        .setAttribute("text-anchor", "middle")
                        .setAttribute("dominant-baseline", "middle");
                    break;
                case "left":
                    offsetX = -8 - this._radius;
                    this._text
                        .setAttribute("text-anchor", "end")
                        .setAttribute("dominant-baseline", "middle");
                    break;
                case "right":
                    offsetX = 8 + this._radius;
                    this._text
                        .setAttribute("text-anchor", "start")
                        .setAttribute("dominant-baseline", "middle");
                    break;
                case "top":
                default:
                    offsetY = -16 - this._radius;
                    this._text
                        .setAttribute("text-anchor", "middle")
                        .setAttribute("dominant-baseline", "middle");
                    break;
            }

            this._feFlood.setAttribute("flood-color", this._color);
            this._text
                .removeAttribute("display")
                .setAttribute("name", `${this._name}_label`)
                .setAttribute("x", this._origin.x + offsetX)
                .setAttribute("y", this._origin.y + offsetY)
                .setAttribute("font-size", "16px")
                .setAttribute("fill", this._colorText)
                .setAttribute("filter", `url(#fill_text_${this._uuid})`)
                .innerText(`${this._label}(${this._position.toArray().map(p => Math.round(parseFloat(p)*100)/100).join(", ")})`);
        } else {
            this._text.setAttribute("display", "none");
        }

        return this;
    }

    static deserialize(graphing, data) {
        const p = new DataPoint(graphing).deserialize(data);

        p._filter.setAttribute("id", `fill_text_${p._uuid}`);
        if ("position" in data) p._position.fromArray(data.position);
        p._radius = "radius" in data ? data.radius : p._radius;
        p._label = "label" in data && "caption" in data.label ? data.label.caption : p._label;
        p._labelPosition = "label" in data && "position" in data.label ? data.label.position : p._labelPosition;

        return p;
    }
}

export default DataPoint;
export { DataPoint };